import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import { format } from 'date-fns';
import { parseApiTimestamp } from '../utils/dateUtils';
import { orderService } from '../services/orderService';
import { quoteService } from '../services/quoteService';
import { packingSlipService } from '../services/packingSlipService';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { shipmentsInService } from '../services/shipmentsInService';
import { dashboardService } from '../services/dashboardService';
import { realtimeService } from '../services/realtimeService';
import { formatCurrency } from '../components/CurrencyTextField';

const formatQuoteNumber = (q) => {
  if (q?.identifier) return q.identifier;
  if (q?.display_number) return q.display_number; // legacy fallback
  if (q?.quote_number == null) return '-';
  const yy = q.quote_date ? String(q.quote_date).slice(2, 4) : new Date().getFullYear().toString().slice(-2);
  return `${yy}${String(q.quote_number).padStart(4, '0')}`;
};

const customerName = (row) =>
  row?.company_name || [row?.customer_first_name, row?.customer_last_name].filter(Boolean).join(' ') || row?.customer_name || '-';

const quoteCreatorName = (q) => {
  const fromEmp = [q?.employee_first_name, q?.employee_last_name].filter(Boolean).join(' ').trim();
  if (fromEmp) return fromEmp;
  const fromQuotedBy = [q?.quoted_by_first_name, q?.quoted_by_last_name].filter(Boolean).join(' ').trim();
  return fromQuotedBy || '—';
};

const quoteTotalForDashboard = (q) => {
  const t = q?.total_amount;
  if (t != null && t !== '') return t;
  const p = q?.proposal_total_amount;
  if (p != null && p !== '') return p;
  return null;
};

const orderCreatorName = (o) => {
  const fromEmp = [o?.employee_first_name, o?.employee_last_name].filter(Boolean).join(' ').trim();
  if (fromEmp) return fromEmp;
  const fromSales = [o?.salesman_first_name, o?.salesman_last_name].filter(Boolean).join(' ').trim();
  return fromSales || '—';
};

const RECONCILE_INTERVAL_MS = 60000;
const POLL_INTERVAL_MS = 3000;
const DASHBOARD_REALTIME_ENABLED = process.env.REACT_APP_DASHBOARD_REALTIME_ENABLED !== 'false';
const CLOCK_TICK_MS = 1000;
const MAX_ORDER_ROWS = 18;
const MAX_QUOTE_ROWS = 18;
const MAX_SHIPMENT_ROWS = 18;
const MAX_PO_ROWS = 14;
const MAX_RECEIVED_ROWS = 14;
const MAX_ACTIVITY_ROWS = 48;

/** API list responses are usually { data: T[] }; tolerate arrays or missing data. */
const listFromResponse = (body) => {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
};

/** Panel chrome for Warehouse Mission Control — matches app primary (teal), not legacy Material blue. */
const DASHBOARD_PANEL_PALETTE = {
  accent: '#00796b',
  headerBg: '#004d40',
  headerText: '#ffffff',
  hoverBg: 'rgba(0, 121, 107, 0.14)',
  rowAltBg: 'rgba(0, 121, 107, 0.06)'
};

const formatEventDate = (dateString) => {
  if (!dateString) return '--/-- --:--';
  try {
    const dt = parseApiTimestamp(dateString);
    if (!dt) return '--/-- --:--';
    if (Number.isNaN(dt.getTime())) return '--/-- --:--';
    return format(dt, 'M/d');
  } catch (err) {
    return '--/--';
  }
};

const formatEventTime = (dateString) => {
  if (!dateString) return '--:--';
  try {
    const dt = parseApiTimestamp(dateString);
    if (!dt) return '--:--';
    if (Number.isNaN(dt.getTime())) return '--:--';
    return format(dt, 'h:mma').toLowerCase();
  } catch (err) {
    return '--:--';
  }
};

/** Activity feed: "04/08 11:23am" in local time */
const formatActivityStamp = (dateString) => {
  if (!dateString) return '--/-- --:--';
  try {
    const dt = parseApiTimestamp(dateString);
    if (!dt || Number.isNaN(dt.getTime())) return '--/-- --:--';
    return format(dt, 'MM/dd h:mma').toLowerCase();
  } catch {
    return '--/-- --:--';
  }
};

const IdentifierLink = ({ label, to, navigate }) => {
  if (!label || label === '-' || !to) {
    return <>{label || '-'}</>;
  }

  return (
    <Link
      component="button"
      type="button"
      underline="always"
      color="inherit"
      onClick={(e) => {
        e.stopPropagation();
        navigate(to);
      }}
      sx={{
        fontSize: 'inherit',
        lineHeight: 'inherit',
        verticalAlign: 'baseline',
        p: 0,
        minWidth: 0,
        fontWeight: 700,
        textAlign: 'left'
      }}
    >
      {label}
    </Link>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clockNow, setClockNow] = useState(new Date());
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentShipments, setRecentShipments] = useState([]);
  const [recentPurchaseOrders, setRecentPurchaseOrders] = useState([]);
  const [recentReceivedShipments, setRecentReceivedShipments] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [loadError, setLoadError] = useState('');

  const loadDashboard = useCallback(async ({ isBackground = false } = {}) => {
    try {
      if (!isBackground) {
        setLoading(true);
      }

      const settled = await Promise.allSettled([
        orderService.getAll({ status: 'open', limit: MAX_ORDER_ROWS }),
        orderService.getAll({ status: 'in_progress', limit: MAX_ORDER_ROWS }),
        orderService.getAll({ status: 'Ready to Ship', limit: MAX_ORDER_ROWS }),
        quoteService.getAll({ limit: MAX_QUOTE_ROWS }),
        packingSlipService.getAll({ limit: MAX_SHIPMENT_ROWS }),
        purchaseOrderService.getAll({ limit: MAX_PO_ROWS }),
        shipmentsInService.getAll({ limit: MAX_RECEIVED_ROWS }),
        dashboardService.getActivityFeed({ limit: MAX_ACTIVITY_ROWS })
      ]);

      const takeList = (i) => {
        const r = settled[i];
        if (r.status === 'fulfilled') return listFromResponse(r.value);
        const msg =
          r.reason?.response?.data?.message ||
          r.reason?.message ||
          (typeof r.reason === 'string' ? r.reason : 'Request failed');
        console.error('Dashboard partial load error:', msg, r.reason);
        return { __error: msg };
      };

      const open = takeList(0);
      const inProgress = takeList(1);
      const readyToShip = takeList(2);
      const failedMessages = [open, inProgress, readyToShip]
        .filter((x) => x && x.__error)
        .map((x) => x.__error);

      const openList = Array.isArray(open) ? open : [];
      const inProgressList = Array.isArray(inProgress) ? inProgress : [];
      const readyList = Array.isArray(readyToShip) ? readyToShip : [];

      const mergedOrders = [...openList, ...inProgressList, ...readyList]
        .sort((a, b) => {
          const da = new Date(a.updated_at || a.order_date || 0).getTime();
          const db = new Date(b.updated_at || b.order_date || 0).getTime();
          return db - da;
        })
        .slice(0, MAX_ORDER_ROWS);

      setRecentOrders(mergedOrders);

      const quotes = takeList(3);
      const slips = takeList(4);
      const pos = takeList(5);
      const receiving = takeList(6);
      const activity = takeList(7);
      [quotes, slips, pos, receiving, activity].forEach((x) => {
        if (x && x.__error) failedMessages.push(x.__error);
      });

      setRecentQuotes((Array.isArray(quotes) ? quotes : []).slice(0, MAX_QUOTE_ROWS));
      setRecentShipments((Array.isArray(slips) ? slips : []).slice(0, MAX_SHIPMENT_ROWS));
      setRecentPurchaseOrders((Array.isArray(pos) ? pos : []).slice(0, MAX_PO_ROWS));
      setRecentReceivedShipments((Array.isArray(receiving) ? receiving : []).slice(0, MAX_RECEIVED_ROWS));

      if (activity && !activity.__error) {
        setActivityFeed(listFromResponse(activity).slice(0, MAX_ACTIVITY_ROWS));
      } else {
        setActivityFeed([]);
      }

      const uniqueErrs = [...new Set(failedMessages.filter(Boolean))];
      if (uniqueErrs.length > 0) {
        setLoadError(
          uniqueErrs.length === 1
            ? uniqueErrs[0]
            : `${uniqueErrs.length} requests failed (e.g. ${uniqueErrs[0]}). Open the browser console for details.`
        );
      } else {
        setLoadError('');
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      setLoadError(err?.response?.data?.message || err?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    let unsubscribeEvents = () => {};
    let unsubscribeConnection = () => {};
    let debounceTimerId = null;
    let intervalId = null;

    if (DASHBOARD_REALTIME_ENABLED) {
      realtimeService.setSubscriptions(['quote', 'work_order', 'packing_slip', 'purchase_order', 'receiving']);
      realtimeService.connect();
      const seenEventIds = new Set();
      const triggerRefresh = () => {
        if (debounceTimerId) {
          window.clearTimeout(debounceTimerId);
        }
        debounceTimerId = window.setTimeout(() => {
          loadDashboard({ isBackground: true });
        }, 300);
      };

      unsubscribeEvents = realtimeService.subscribe((event) => {
        if (event?.eventId) {
          if (seenEventIds.has(event.eventId)) return;
          if (seenEventIds.size > 200) {
            const first = seenEventIds.values().next().value;
            seenEventIds.delete(first);
          }
          seenEventIds.add(event.eventId);
        }

        const trackedEntities = new Set(['quote', 'work_order', 'packing_slip', 'purchase_order', 'receiving']);
        if (trackedEntities.has(event?.entity)) {
          triggerRefresh();
        }
      });

      unsubscribeConnection = realtimeService.onConnectionStateChange((state) => {
        setConnectionState(state);
      });

      intervalId = window.setInterval(() => {
        loadDashboard({ isBackground: true });
      }, RECONCILE_INTERVAL_MS);
    } else {
      setConnectionState('polling_only');
      intervalId = window.setInterval(() => {
        loadDashboard({ isBackground: true });
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      if (debounceTimerId) {
        window.clearTimeout(debounceTimerId);
      }
      unsubscribeEvents();
      unsubscribeConnection();
      if (DASHBOARD_REALTIME_ENABLED) {
        realtimeService.setSubscriptions([]);
        realtimeService.disconnect();
      }
    };
  }, [loadDashboard]);

  useEffect(() => {
    const tickId = window.setInterval(() => {
      setClockNow(new Date());
    }, CLOCK_TICK_MS);
    return () => window.clearInterval(tickId);
  }, []);

  const EventTable = ({ title, items, onRowClick, columns, emptyText, palette = {} }) => (
    <Paper
      sx={{
        p: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 0.2,
          backgroundColor: palette.headerBg || '#004d40',
          borderTop: `3px solid ${palette.accent || '#00796b'}`
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 400, color: palette.headerText || '#fff' }}>
          {title}
        </Typography>
      </Box>
      {loading ? (
        <Box py={1.5} display="flex" justifyContent="center">
          <CircularProgress size={20} />
        </Box>
      ) : (
        <TableContainer sx={{ minHeight: 0, overflowY: 'auto', p: 1 }}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableBody>
              {!items.length ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    sx={{ color: 'text.secondary', py: 0.2, px: 0.6, fontSize: '0.78rem' }}
                  >
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, idx) => (
                  <TableRow
                    hover
                    key={item.id || idx}
                    onClick={() => onRowClick(item)}
                    sx={{
                      cursor: 'pointer',
                      height: 20,
                      '&:nth-of-type(odd)': {
                        backgroundColor: palette.rowAltBg || 'rgba(0, 121, 107, 0.06)'
                      },
                      '&:hover': {
                        backgroundColor: palette.hoverBg || 'rgba(0, 121, 107, 0.14)'
                      }
                    }}
                  >
                    {columns.map((column, colIdx) => (
                      <TableCell
                        key={`${item.id || idx}-${column.key}`}
                        sx={{
                          width: column.width,
                          maxWidth: column.width,
                          minWidth: column.minWidth || 0,
                          whiteSpace: 'nowrap',
                          overflow: column.noTruncate ? 'visible' : 'hidden',
                          textOverflow: column.noTruncate ? 'clip' : 'ellipsis',
                          fontSize: '0.76rem',
                          lineHeight: 1.05,
                          py: 0.12,
                          px: 0.6,
                          fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif',
                          borderBottom: '1px solid rgba(0, 77, 64, 0.1)',
                          borderRight: colIdx < columns.length - 1 ? '1px solid rgba(0, 77, 64, 0.08)' : 'none'
                        }}
                      >
                        <Box
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {column.render(item)}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  return (
    <Box
      className="no-print-dashboard"
      sx={{
        height: 'calc(100vh - 72px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        gap: 1
      }}
    >
      <Paper sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                color: 'primary.dark'
              }}
            >
              Warehouse Mission Control
            </Typography>
            {loadError && (
              <Alert severity="warning" sx={{ mt: 1, py: 0.25 }} onClose={() => setLoadError('')}>
                {loadError}
                {' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  Other sections may still show data. If you pulled new code, run pending database migrations.
                </Typography>
              </Alert>
            )}
          </Box>
          <Box>
            <Typography
              variant="body2"
              component="div"
              sx={{ fontWeight: 400, lineHeight: 1.2, color: 'text.secondary', fontSize: '0.85rem' }}
            >
              {format(clockNow, 'M/d h:mm:ssa').toLowerCase()}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{ color: connectionState === 'connected' ? 'success.main' : 'text.secondary' }}
            >
              Realtime: {connectionState}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'grid',
          gap: 1,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(6, minmax(0, 1fr))' },
          gridTemplateRows: {
            xs: 'minmax(120px, 0.32fr) repeat(5, minmax(0, 1fr))',
            md: 'minmax(96px, 18vh) repeat(2, minmax(0, 1fr))'
          }
        }}
      >
        <Box
          sx={{
            minHeight: 0,
            gridColumn: { xs: '1 / -1', md: '1 / -1' },
            gridRow: { md: '1' }
          }}
        >
          <Paper
            sx={{
              p: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                px: 1,
                py: 0.2,
                backgroundColor: DASHBOARD_PANEL_PALETTE.headerBg,
                borderTop: `3px solid ${DASHBOARD_PANEL_PALETTE.accent}`
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 400, color: DASHBOARD_PANEL_PALETTE.headerText }}>
                Recent activity
              </Typography>
            </Box>
            {loading ? (
              <Box py={1.5} display="flex" justifyContent="center">
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  px: 1,
                  py: 0.75,
                  fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif'
                }}
              >
                {!activityFeed.length ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                    No recent activity yet.
                  </Typography>
                ) : (
                  activityFeed.map((item) => (
                    <Typography
                      key={item.id}
                      variant="body2"
                      component="div"
                      sx={{
                        fontSize: '0.76rem',
                        lineHeight: 1.35,
                        py: 0.2,
                        borderBottom: '1px solid rgba(0, 77, 64, 0.1)',
                        '&:last-of-type': { borderBottom: 'none' }
                      }}
                    >
                      <Box component="span" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                        {formatActivityStamp(item.created_at)}
                      </Box>
                      {' - '}
                      {item.message}
                    </Typography>
                  ))
                )}
              </Box>
            )}
          </Paper>
        </Box>
        <Box sx={{ minHeight: 0, gridColumn: { md: '1 / 4' }, gridRow: { md: '2' } }}>
          <EventTable
            title="Quotes"
            items={recentQuotes}
            emptyText="No recent quotes"
            onRowClick={(q) => navigate(`/quotes/${q.id}`)}
            columns={[
              { key: 'date', width: '11%', render: (q) => formatEventDate(q.updated_at || q.created_at || q.quote_date) },
              { key: 'time', width: '12%', minWidth: 72, noTruncate: true, render: (q) => formatEventTime(q.updated_at || q.created_at || q.quote_date) },
              { key: 'customer', width: '26%', render: (q) => customerName(q) },
              { key: 'creator', width: '18%', render: (q) => quoteCreatorName(q) },
              {
                key: 'total',
                width: '13%',
                render: (q) => (
                  <Box component="span" sx={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(quoteTotalForDashboard(q))}
                  </Box>
                )
              },
              {
                key: 'identifier',
                width: '20%',
                render: (q) => <IdentifierLink label={formatQuoteNumber(q)} to={`/quotes/${q.id}`} navigate={navigate} />
              }
            ]}
            palette={DASHBOARD_PANEL_PALETTE}
          />
        </Box>
        <Box sx={{ minHeight: 0, gridColumn: { md: '4 / 7' }, gridRow: { md: '2' } }}>
          <EventTable
            title="Orders"
            items={recentOrders}
            emptyText="No recent orders"
            onRowClick={(o) => navigate(`/work-orders/${o.id}`)}
            columns={[
              { key: 'date', width: '10%', render: (o) => formatEventDate(o.updated_at || o.order_date) },
              { key: 'time', width: '11%', minWidth: 72, noTruncate: true, render: (o) => formatEventTime(o.updated_at || o.order_date) },
              { key: 'customer', width: '22%', render: (o) => customerName(o) },
              { key: 'creator', width: '15%', render: (o) => orderCreatorName(o) },
              {
                key: 'total',
                width: '12%',
                render: (o) => (
                  <Box component="span" sx={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(o?.total_amount)}
                  </Box>
                )
              },
              { key: 'status', width: '14%', render: (o) => o?.status || '—' },
              {
                key: 'identifier',
                width: '16%',
                render: (o) => (
                  <IdentifierLink
                    label={o.identifier || o.work_order_number || '-'}
                    to={`/work-orders/${o.id}`}
                    navigate={navigate}
                  />
                )
              }
            ]}
            palette={DASHBOARD_PANEL_PALETTE}
          />
        </Box>
        <Box sx={{ minHeight: 0, gridColumn: { md: '1 / 3' }, gridRow: { md: '3' } }}>
          <EventTable
            title="Shipments"
            items={recentShipments}
            emptyText="No recent shipments"
            onRowClick={(s) => navigate(`/packing-slips/${s.id}`)}
            columns={[
              { key: 'date', width: '14%', render: (s) => formatEventDate(s.shipped_date || s.created_at) },
              { key: 'time', width: '18%', minWidth: 88, noTruncate: true, render: (s) => formatEventTime(s.shipped_date || s.created_at) },
              { key: 'customer', width: '32%', render: (s) => s.customer_name || '-' },
              {
                key: 'slip',
                width: '18%',
                render: (s) => (
                  <IdentifierLink
                    label={s.identifier || s.packing_slip_number || '-'}
                    to={`/packing-slips/${s.id}`}
                    navigate={navigate}
                  />
                )
              },
              {
                key: 'order',
                width: '18%',
                render: (s) => (
                  <IdentifierLink
                    label={s.work_order_identifier || s.work_order_number || 'WO -'}
                    to={s.work_order_id ? `/work-orders/${s.work_order_id}` : null}
                    navigate={navigate}
                  />
                )
              }
            ]}
            palette={DASHBOARD_PANEL_PALETTE}
          />
        </Box>
        <Box sx={{ minHeight: 0, gridColumn: { md: '3 / 5' }, gridRow: { md: '3' } }}>
          <EventTable
            title="Purchase Orders"
            items={recentPurchaseOrders}
            emptyText="No recent purchase orders"
            onRowClick={(po) => navigate(`/procurement/po/${po.id}`)}
            columns={[
              { key: 'date', width: '16%', render: (po) => formatEventDate(po.updated_at || po.created_at || po.po_date) },
              { key: 'time', width: '20%', minWidth: 88, noTruncate: true, render: (po) => formatEventTime(po.updated_at || po.created_at || po.po_date) },
              { key: 'vendor', width: '40%', render: (po) => po.vendor_name || '-' },
              {
                key: 'po',
                width: '24%',
                render: (po) => (
                  <IdentifierLink
                    label={po.identifier || po.po_number || '-'}
                    to={`/procurement/po/${po.id}`}
                    navigate={navigate}
                  />
                )
              }
            ]}
            palette={DASHBOARD_PANEL_PALETTE}
          />
        </Box>
        <Box sx={{ minHeight: 0, gridColumn: { md: '5 / 7' }, gridRow: { md: '3' } }}>
          <EventTable
            title="Shipments Received"
            items={recentReceivedShipments}
            emptyText="No received shipments"
            onRowClick={() => navigate('/procurement/shipments-in')}
            columns={[
              { key: 'date', width: '14%', render: (r) => formatEventDate(r.updated_at || r.created_at || r.receiver_date) },
              { key: 'time', width: '18%', minWidth: 88, noTruncate: true, render: (r) => formatEventTime(r.updated_at || r.created_at || r.receiver_date) },
              { key: 'vendor', width: '24%', render: (r) => r.vendor_name || r.name || '-' },
              {
                key: 'po',
                width: '22%',
                render: (r) => (
                  <IdentifierLink
                    label={r.po_identifier || r.po_number || 'PO -'}
                    to={r.purchase_order_id ? `/procurement/po/${r.purchase_order_id}` : null}
                    navigate={navigate}
                  />
                )
              },
              {
                key: 'receiving',
                width: '22%',
                render: (r) => (
                  <IdentifierLink
                    label={r.identifier || r.receiver_no || '-'}
                    to={`/procurement/shipments-in/${r.id}`}
                    navigate={navigate}
                  />
                )
              }
            ]}
            palette={DASHBOARD_PANEL_PALETTE}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
